/**
 * NeutralizationBarChart — token-driven bar chart for per-theorem
 * neutralization counts, attack-surface deltas, governance budgets, and
 * any other categorical-axis metric the demo library surfaces.
 *
 * The component wraps Recharts in a Responsive container so callers
 * embed it inside a SecuritySurfacePanel slot (or any flexbox surface)
 * without forcing an explicit width. Bar fill colours resolve through
 * {@link theoremSeriesColor} so a renamed or recoloured theorem in
 * `tokens.data.json` propagates here automatically — never hard-coded
 * hex literals.
 *
 * Each datum carries an optional `color` override for non-theorem
 * categories (e.g. `vulnerable` vs `csk` in DifferentialSecurity) so the
 * same primitive serves the full set of metric panels referenced in
 * issue #1581.
 *
 * Accessibility: the chart sets `role="img"` with an `aria-label`
 * derived from the props. Recharts itself emits SVG `<title>` tags for
 * each bar so screen readers can interrogate individual values.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type BarShapeProps,
} from "recharts";
import {
  CHART_FONT,
  DEFAULT_CHART_PALETTE,
  type ChartPalette,
  theoremSeriesColor,
} from "./ChartTheme.js";

export interface NeutralizationBarDatum {
  /** Category label rendered on the X axis (e.g. theorem name). */
  readonly label: string;
  /** Bar height in the chart's value units. */
  readonly value: number;
  /**
   * Optional explicit fill colour. When omitted, the chart resolves a
   * theorem-keyed colour via {@link theoremSeriesColor}.
   */
  readonly color?: string;
}

export interface NeutralizationBarChartProps {
  /** Bars to render, one per category. */
  readonly data: readonly NeutralizationBarDatum[];
  /** Pixel height for the responsive container. Defaults to 220. */
  readonly height?: number;
  /** Y-axis label rendered above the value column. */
  readonly valueLabel?: string;
  /** Accessible label announced by assistive tech. */
  readonly ariaLabel: string;
  /** Palette override; falls back to {@link DEFAULT_CHART_PALETTE}. */
  readonly palette?: ChartPalette;
}

type Domain = readonly [number, number];

const DEFAULT_RESPONSIVE_WIDTH = 640;

function padUpper(max: number): number {
  // Round headroom up to the next sensible tick so axes don't crop bars.
  // For tiny ranges we floor to 1 to keep the tick density readable.
  return max <= 4 ? Math.max(max + 1, 1) : Math.ceil(max * 1.1);
}

function padLower(min: number): number {
  // Mirror the upper-bound padding logic for negative deltas. The
  // `Math.min` keeps the floor strictly below the most-negative bar.
  return min >= -4 ? Math.min(min - 1, -1) : Math.floor(min * 1.1);
}

function computeDomain(data: readonly NeutralizationBarDatum[]): Domain {
  let max = 0;
  let min = 0;
  for (const datum of data) {
    if (datum.value > max) max = datum.value;
    if (datum.value < min) min = datum.value;
  }
  // Counts, budgets, and capability tallies are non-negative — the
  // bar-chart primitive also serves attack-surface deltas, which can
  // dip below zero. When every bar is non-negative we keep the floor
  // pinned at 0 (the common case); when any bar is negative we extend
  // the lower bound symmetrically so the baseline is visible.
  const lower = min < 0 ? padLower(min) : 0;
  const upper = padUpper(max);
  return [lower, upper];
}

function resolveDatumFill(datum: NeutralizationBarDatum, palette: ChartPalette): string {
  return datum.color ?? theoremSeriesColor(datum.label, palette);
}

/**
 * Per-bar shape renderer. Recharts 3 deprecates the `<Cell>` child
 * pattern; the recommended replacement is a custom shape function on
 * the `<Bar>` that consults each datum's payload for its fill. This
 * keeps the rectangle styling but flips the colour from the payload,
 * which is exactly what we want for token-aligned categorical bars.
 */
function ColoredBarShape(props: BarShapeProps): React.JSX.Element {
  const fill =
    typeof props.fill === "string" && props.fill.length > 0
      ? props.fill
      : ((props.payload as { readonly fill?: string } | undefined)?.fill ?? "currentColor");
  return (
    <Rectangle
      x={props.x}
      y={props.y}
      width={props.width}
      height={props.height}
      {...(props.radius !== undefined && { radius: props.radius })}
      fill={fill}
    />
  );
}

export const NeutralizationBarChart = React.memo(function NeutralizationBarChart({
  data,
  height = 220,
  valueLabel,
  ariaLabel,
  palette = DEFAULT_CHART_PALETTE,
}: NeutralizationBarChartProps): React.JSX.Element {
  const domain = useMemo(() => computeDomain(data), [data]);
  const items = useMemo(
    () =>
      data.map((datum) => ({
        ...datum,
        fill: resolveDatumFill(datum, palette),
      })),
    [data, palette],
  );

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-csk-chart="neutralization-bar"
      style={{ width: "100%", height }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={0}
        minHeight={height}
        initialDimension={{ width: DEFAULT_RESPONSIVE_WIDTH, height }}
      >
        <BarChart data={items} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            stroke={palette.axisLine}
            tick={{ fill: palette.axis, fontSize: CHART_FONT.axisSize }}
            tickLine={false}
          />
          <YAxis
            stroke={palette.axisLine}
            tick={{ fill: palette.axis, fontSize: CHART_FONT.axisSize }}
            tickLine={false}
            domain={domain}
            allowDecimals={false}
            {...(valueLabel !== undefined && {
              label: {
                value: valueLabel,
                angle: -90,
                position: "insideLeft",
                fill: palette.axis,
                fontSize: CHART_FONT.axisSize,
              },
            })}
          />
          <Tooltip
            cursor={{ fill: palette.grid, opacity: 0.3 }}
            contentStyle={{
              background: palette.tooltipBg,
              border: `1px solid ${palette.tooltipBorder}`,
              color: palette.tooltipText,
              fontSize: CHART_FONT.tooltipSize,
              borderRadius: 6,
            }}
          />
          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            shape={ColoredBarShape}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});
